import { useMemo, useState, type CSSProperties, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus, Clock, MapPin, Phone, RotateCcw, X } from 'lucide-react';
import { api, ApiError, errorMessage } from '../lib/api';
import { addDays, longDate, shortDate, time12 } from '../lib/dates';
import { classNames, inkFor, plural, spelledReference } from '../lib/format';
import type { FloorPlan, GuestBooking, PublicVenue, Slot } from '../lib/types';
import { FloorMap } from '../map/FloorMap';
import { WayfindingList } from '../map/Wayfinding';
import { ConfirmDialog, Field, Notice, Spinner, StatusPill, useTitle, useToast } from '../ui';
import { DateStrip } from './DatePicker';
import { rememberBooking } from './guestMemory';

interface VenueResponse {
  venue: PublicVenue;
  today: string;
  floor_plans: FloorPlan[];
}

export function ManageBooking() {
  const { reference = '' } = useParams();
  const [search] = useSearchParams();
  const token = search.get('token') ?? '';
  const q = useQuery({
    queryKey: ['manage', reference, token],
    queryFn: () => api<{ booking: GuestBooking }>(`/api/public/bookings/${encodeURIComponent(reference)}`, { query: { token } }),
    enabled: Boolean(token),
  });
  const slug = q.data?.booking.venue.slug;
  const venueQ = useQuery({
    queryKey: ['public-venue', slug],
    queryFn: () => api<VenueResponse>(`/api/public/venues/${slug}`),
    enabled: Boolean(slug),
  });
  useTitle(`Your booking${q.data ? ` · ${q.data.booking.venue.name}` : ''}`);

  if (!token) return <FindBooking initialReference={reference} />;
  if (q.isPending) {
    return (
      <div className="manage-loading">
        <Spinner />
      </div>
    );
  }
  if (q.isError) {
    return (
      <div className="bk-missing">
        <h1 className="display">Booking not found</h1>
        <p className="muted">{errorMessage(q.error)}</p>
        <p style={{ marginTop: 16 }}>
          <Link className="btn" to="/booking/find">
            Find it with your reference
          </Link>
        </p>
      </div>
    );
  }
  return <ManageView booking={q.data.booking} token={token} venueData={venueQ.data} />;
}

function ManageView({ booking, token, venueData }: { booking: GuestBooking; token: string; venueData?: VenueResponse }) {
  const venue = booking.venue;
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [changing, setChanging] = useState(false);
  const [activeRoute, setActiveRoute] = useState<string | null>(null);
  const brandStyle = { '--brand': venue.brand_color, '--brand-ink': inkFor(venue.brand_color) } as CSSProperties;

  const plan = useMemo(() => {
    const planId = booking.tables[0]?.floor_plan_id;
    return venueData?.floor_plans.find((p) => p.id === planId) ?? venueData?.floor_plans[0];
  }, [venueData, booking.tables]);
  const table = booking.tables.length === 1 ? plan?.tables.find((t) => t.id === booking.tables[0]!.id) : undefined;
  const cancelled = booking.status === 'cancelled';

  const refresh = (updated: GuestBooking) => {
    queryClient.setQueryData(['manage', booking.reference, token], { booking: updated });
  };

  const cancel = async () => {
    setBusy(true);
    try {
      const res = await api<{ booking: GuestBooking }>(`/api/public/bookings/${booking.reference}/cancel`, { method: 'POST', body: { token } });
      refresh(res.booking);
      toast('Your booking has been cancelled.');
      setCancelOpen(false);
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  };

  const bookAgain = () => {
    const params = new URLSearchParams({ party: String(booking.party_size) });
    if (booking.tables[0]) params.set('table', booking.tables[0].id);
    navigate(`/book/${venue.slug}?${params}`);
  };

  return (
    <div className="bk manage" style={brandStyle}>
      <header className="bk-hero">
        <div className="bk-hero-inner">
          <div className="eyebrow bk-kind">Your booking</div>
          <h1 className="display bk-name">{venue.name}</h1>
          <div className="bk-meta">
            {venue.address && (
              <span>
                <MapPin /> {venue.address}
              </span>
            )}
            {venue.phone && (
              <a href={`tel:${venue.phone.replace(/\s/g, '')}`}>
                <Phone /> {venue.phone}
              </a>
            )}
          </div>
        </div>
      </header>
      <main className="manage-main">
        <section className="manage-panel">
          <div className="row between" style={{ marginBottom: 6 }}>
            <StatusPill status={booking.status} />
            <span className="mono faint">{spelledReference(booking.reference)}</span>
          </div>
          <h2 className="display manage-when">
            {longDate(booking.date)}
            <br />
            {time12(booking.time)} – {time12(booking.end_time)}
          </h2>
          <div className="manage-facts">
            <span>{plural(booking.party_size, 'guest')}</span>
            {booking.tables.length > 0 && <span>Table {booking.tables.map((t) => t.label).join(' + ')}</span>}
            {booking.guest_name && <span>For {booking.guest_name}</span>}
          </div>
          {booking.guest_notes && <p className="manage-notes">“{booking.guest_notes}”</p>}

          {cancelled ? (
            <Notice tone="info" className="manage-notice">
              This booking was cancelled. We hope to see you another time.
            </Notice>
          ) : !booking.can_change && booking.change_message ? (
            <Notice tone="info" className="manage-notice">
              {booking.change_message}
            </Notice>
          ) : null}

          <div className="manage-actions">
            {!cancelled && (
              <a className="btn" href={`/api/public/bookings/${booking.reference}/calendar.ics?token=${encodeURIComponent(token)}`}>
                <CalendarPlus /> Add to calendar
              </a>
            )}
            {booking.can_change && (
              <button className="btn" onClick={() => setChanging((c) => !c)}>
                <Clock /> Change date or time
              </button>
            )}
            {booking.can_change && (
              <button className="btn btn-danger" onClick={() => setCancelOpen(true)}>
                <X /> Cancel booking
              </button>
            )}
            <button className="btn btn-ghost" onClick={bookAgain}>
              <RotateCcw /> Book again
            </button>
          </div>

          {changing && venueData && (
            <ChangeTime
              booking={booking}
              token={token}
              today={venueData.today}
              onDone={(updated) => {
                refresh(updated);
                rememberBooking(venue.slug, {
                  reference: updated.reference,
                  token,
                  date: updated.date,
                  time: updated.time,
                  party_size: updated.party_size,
                  table_ids: updated.tables.map((t) => t.id),
                  table_labels: updated.tables.map((t) => t.label),
                  venue_name: venue.name,
                });
                setChanging(false);
                toast(`Moved to ${shortDate(updated.date)} at ${time12(updated.time)}.`);
              }}
            />
          )}

          {table && table.wayfinding.length > 0 && !cancelled && (
            <div className="manage-way">
              <div className="eyebrow" style={{ marginBottom: 8 }}>
                Finding your way
              </div>
              <WayfindingList table={table} active={activeRoute} onActive={setActiveRoute} />
            </div>
          )}
        </section>
        <section className="manage-map">
          {plan ? (
            <FloorMap
              plan={plan}
              className="bk-map"
              selectedIds={cancelled ? [] : booking.tables.map((t) => t.id)}
              tableClass={(t) => (booking.tables.some((x) => x.id === t.id) && !cancelled ? 't-available' : 't-neutral')}
              routesFor={!cancelled && table ? table : null}
              activeRoute={activeRoute}
            />
          ) : (
            <div className="skeleton" style={{ height: '100%', borderRadius: 14 }} />
          )}
        </section>
      </main>
      <ConfirmDialog
        open={cancelOpen}
        title="Cancel this booking?"
        confirmLabel="Cancel booking"
        danger
        busy={busy}
        onConfirm={cancel}
        onClose={() => setCancelOpen(false)}
      >
        Your table for {booking.party_size} on {longDate(booking.date)} at {time12(booking.time)} will be released.
      </ConfirmDialog>
    </div>
  );
}

function ChangeTime(props: { booking: GuestBooking; token: string; today: string; onDone: (b: GuestBooking) => void }) {
  const { booking } = props;
  const rules = booking.venue.booking_rules;
  const [date, setDate] = useState(booking.date);
  const [party, setParty] = useState(booking.party_size);
  const [time, setTime] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const slotsQ = useQuery({
    queryKey: ['availability', booking.venue.slug, date, party],
    queryFn: () => api<{ slots: Slot[]; closed: boolean }>(`/api/public/venues/${booking.venue.slug}/availability`, { query: { date, party_size: party } }),
  });
  const calQ = useQuery({
    queryKey: ['calendar', booking.venue.slug, party, []],
    queryFn: async () =>
      new Map(
        (await api<{ days: any[] }>(`/api/public/venues/${booking.venue.slug}/calendar`, { query: { days: 14, party_size: party } })).days.map((d) => [d.date, d]),
      ),
  });
  const save = async () => {
    if (!time) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ booking: GuestBooking }>(`/api/public/bookings/${booking.reference}`, {
        method: 'PATCH',
        body: { token: props.token, date, time, party_size: party },
      });
      props.onDone(res.booking);
    } catch (err) {
      setError(err instanceof ApiError && err.details?.alternatives?.length ? `${err.message} Try ${err.details.alternatives.map(time12).join(', ')}.` : errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  // The booking's own slot counts against itself on the server only when saving,
  // so we show its current time as available here.
  const slots = (slotsQ.data?.slots ?? []).map((s) => (date === booking.date && s.time === booking.time ? { ...s, available: true } : s));
  return (
    <div className="manage-change">
      <Field label="Guests">
        {(id) => (
          <select id={id} className="select" value={party} onChange={(e) => setParty(Number(e.target.value))} style={{ maxWidth: 140 }}>
            {Array.from({ length: rules.max_party - rules.min_party + 1 }, (_, i) => i + rules.min_party).map((n) => (
              <option key={n} value={n}>
                {plural(n, 'guest')}
              </option>
            ))}
          </select>
        )}
      </Field>
      <DateStrip
        today={props.today}
        value={date}
        onChange={(d) => {
          setDate(d);
          setTime(null);
        }}
        days={calQ.data ?? new Map()}
        maxDate={addDays(props.today, rules.booking_window_days)}
      />
      <div className="slots" style={{ marginTop: 12 }}>
        {slotsQ.isPending && <Spinner />}
        {slots.map((s) => (
          <button key={s.time} type="button" className="slot num" disabled={!s.available} aria-pressed={time === s.time} onClick={() => setTime(s.time)}>
            {s.time}
          </button>
        ))}
        {slotsQ.data?.closed && <span className="muted">Closed that day.</span>}
      </div>
      {error && (
        <Notice tone="danger" className="manage-notice">
          {error}
        </Notice>
      )}
      <button className="btn btn-primary" style={{ marginTop: 14 }} disabled={!time || busy} onClick={save}>
        {busy && <Spinner />} Move booking{time ? ` to ${shortDate(date)}, ${time12(time)}` : ''}
      </button>
    </div>
  );
}

export function FindBooking({ initialReference = '' }: { initialReference?: string }) {
  const [reference, setReference] = useState(initialReference);
  const [contact, setContact] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  useTitle('Find your booking');
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ booking: GuestBooking; manage_token: string }>('/api/public/bookings/find', {
        method: 'POST',
        body: { reference, contact },
      });
      navigate(`/booking/${res.booking.reference}?token=${encodeURIComponent(res.manage_token)}`, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="find">
      <form className="card card-pad find-card" onSubmit={submit}>
        <h1 className="display" style={{ fontSize: 28 }}>
          Find your booking
        </h1>
        <p className="muted" style={{ margin: '6px 0 20px' }}>
          Enter the reference from your confirmation and the email or mobile number you booked with.
        </p>
        <div className="stack gap-4">
          <Field label="Booking reference">
            {(id) => (
              <input
                id={id}
                className="input mono"
                value={reference}
                onChange={(e) => setReference(e.target.value.toUpperCase())}
                placeholder="e.g. K7Q M4D"
                autoComplete="off"
                required
              />
            )}
          </Field>
          <Field label="Email or mobile">
            {(id) => <input id={id} className="input" value={contact} onChange={(e) => setContact(e.target.value)} autoComplete="email" required />}
          </Field>
          {error && <Notice tone="danger">{error}</Notice>}
          <button className={classNames('btn btn-dark btn-lg')} disabled={busy}>
            {busy && <Spinner />} Find booking
          </button>
        </div>
      </form>
    </div>
  );
}
